import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import DOMPurify from 'dompurify';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { stripHtmlToText } from '@app/shared/strip-html-to-text';

@Pipe({
  name: 'filesize',
  standalone: false,
})
export class FileSize implements PipeTransform {
  transform(value) {
    let size = parseInt(value);
    if (size > 1000 && size < 1000000) {
      size = size / 1000;
      return size.toFixed(2) + 'kb';
    } else if (size > 1000000) {
      size = size / 1000000;
      return size.toFixed(2) + 'mb';
    } else return size + 'b';
  }
}
@Pipe({
  name: 'safe',
  standalone: false,
})
export class SafePipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}
  transform(url) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}

@Pipe({
  name: 'safeHtml',
  standalone: false,
})
export class SafeHTML implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(html: string | SafeHtml | null | undefined): SafeHtml {
    let htmlString: string;
    if (html == null || html === 'null' || html === 'undefined') {
      htmlString = '';
    } else if (typeof html === 'string') {
      htmlString = html;
    } else {
      const unwrapped = this.sanitizer.sanitize(
        SecurityContext.HTML,
        html as any
      );
      htmlString = unwrapped ?? '';
    }
    DOMPurify.addHook('afterSanitizeAttributes', function (node) {
      // set all elements owning target to target=_blank
      if ('target' in node) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    let sanitized = DOMPurify.sanitize(htmlString, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: [
        'allow',
        'allowfullscreen',
        'frameborder',
        'scrolling',
        'target',
      ],
    });
    // Remove any style tags to prevent them from affecting the entire page
    let previous: string;
    do {
      previous = sanitized;
      sanitized = sanitized.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    } while (sanitized !== previous);
    return this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }
}

@Pipe({
  name: 'sanitizeHtmlTooltip',
  standalone: false,
})
export class SanitizeHtmlTooltipPipe implements PipeTransform {
  transform(html: string): string {
    if (!html || html === 'null' || html === 'undefined' || typeof html !== 'string') {
      return '';
    }
    const textOnly = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
    });
    return this.decodeHtmlEntities(textOnly);
  }

  /** After tags are removed, turn entities back into characters for readable tooltips. */
  private decodeHtmlEntities(text: string): string {
    if (typeof document === 'undefined') {
      return text;
    }
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }
}

@Pipe({
  name: 'safeStyle',
  standalone: false,
})
export class SafeStyles implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string) {
    if (!value || !this.isSafeStyle(value)) {
      return '';
    }

    return this.sanitizer.bypassSecurityTrustStyle(value);
  }

  private isSafeStyle(value: string): boolean {
    const allowedProperties = [
      'background',
      'background-color',
      'border',
      'border-radius',
      'text-align',
      'justify-content',
      'align-items',
      'padding',
      'padding-top',
      'padding-bottom',
      'padding-left',
      'padding-right',
      'color',
      'font-style',
      'font-weight',
      'width',
      'height',
    ];

    const styles = value
      .split(';')
      .map(s => s.trim())
      .filter(s => s);

    for (const style of styles) {
      const [property] = style.split(':').map(s => s.trim());

      if (!allowedProperties.includes(property)) {
        return false;
      }
    }

    return true;
  }
}

@Pipe({
  name: 'filter',
  standalone: false,
})
export class TextFilter implements PipeTransform {
  transform(arr, key, value) {
    return arr.filter(i => i[key] == value);
  }
}
@Pipe({
  name: 'truncate',
  standalone: false,
})
export class Truncate implements PipeTransform {
  transform(text: string, length = '200') {
    if (!text) return '';
    if (!text?.substr) return text;
    const maxLength = parseInt(length);
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '..';
  }
}

/** Strips HTML tags and decodes common entities to plain text. Use for tooltips when content may be rich HTML. */
@Pipe({
  name: 'stripHtml',
  standalone: false,
})
export class StripHtmlPipe implements PipeTransform {
  transform(html: string): string {
    return stripHtmlToText(html);
  }
}

@Pipe({
  name: 'formatText',
  standalone: false,
})
export class FormatText implements PipeTransform {
  transform(text: string) {
    if (!text) return '';
    return text.replace(/(?:\\r\\n|\\r|\\n)/g, '<br>');
  }
}

@Pipe({
  name: 'friendlyType',
  standalone: false,
})
export class FriendlyType implements PipeTransform {
  types: Object = {
    roles: 'Role',
    resources: 'Resource',
    gradecategories: 'Grade Category',
    quizquestions: 'Quiz Question',
    dashboard: 'Dashboard',
    files: 'File',
    objectives: 'Objective',
    quizbanks: 'Quiz Bank',
    gradeschemas: 'Grade Schema',
    gradecolumns: 'Grade Column',
    announcements: 'Announcement',
    groupsets: 'Group Set',
    groups: 'Group',
    lti: 'Tool',
    assignmentgroups: 'Assignment Group',
    exceptionlogentry: 'Exception Log Entry',
    learningcomponent: 'Learning Component',
    quizanswers: 'Quiz Answer',
    quizgroups: 'Quiz Group',
    gradeentry: 'Grade Entry',
    gradescales: 'Gradescale',
    rubrics: 'Rubric',
    filefolders: 'File Folder',
    test: 'Test',
    moduleitems: 'Moduleitem',
    outcomes: 'Outcome',
    assignments: 'Assignment',
    discussions: 'Discussion',
    modules: 'Module',
    users: 'User',
    courses: 'Courses',
    pages: 'Page',
    quizzes: 'Quizzes',
  };

  constructor() {}

  toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
  transform(text: string) {
    if (!text) return '';
    if (this.types.hasOwnProperty(text)) {
      text = this.types[text];
    } else {
      text = this.toTitleCase(text?.replace(/__c/, ''));
    }
    return text;
  }
}

@Pipe({
  name: 'translationFromString',
  pure: false,
  standalone: false,
})
export class TranslationFromStringPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  capitalizeEachWord(input) {
    if (typeof input !== 'string') return input;
    const words = input.split(' ');
    const capitalizedWords = words.map(
      word => word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase()
    );
    return capitalizedWords.join(' ');
  }

  transform(input = '') {
    if (input === undefined) input = '';
    const pattern =
      /\[\s*([^|\]]*?)\s*\|\s*translation\s*(?:\|\s*([^|\]]*?)\s*)?]/g;
    return !input.replace
      ? input
      : input.replace(pattern, (_, key, additionalFormatArgs) => {
          const trimmedKey = key.trim();
          let content = this.translateService.instant(trimmedKey);
          if (!content) return trimmedKey;
          additionalFormatArgs = additionalFormatArgs?.trim();
          if (additionalFormatArgs === 'uppercase')
            content = content.toUpperCase();
          if (additionalFormatArgs === 'lowercase')
            content = content.toLowerCase();
          if (additionalFormatArgs === 'capitalize')
            content = this.capitalizeEachWord(content);
          return content;
        });
  }
}

@Pipe({
  name: 'translation',
  pure: false,
  standalone: false,
})
export class TranslationPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(key): any {
    if (typeof key === 'number' && !isNaN(key)) {
      return key;
    }
    if (!key || key === 'undefined') return '';
    let content = '';
    try {
      content = this.translateService.instant(key);
    } catch (error) {
      console.log('error in translation pipe: ' + error);
    }
    if (!content || content === key) {
      return this.snakeCaseToTitleCase(key);
    }
    return content;
  }

  private snakeCaseToTitleCase(str: string): string {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}

@Pipe({
  name: 'translationDate',
  pure: false,
  standalone: false,
})
export class TranslationDatePipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(dateString: string): string {
    if (!dateString || typeof dateString !== 'string') return dateString;

    const monthNames = [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'september',
      'october',
      'november',
      'december',
    ];

    const parts = dateString.split(' ');

    if (parts.length === 0) return dateString;

    let translatedParts = parts.map(part => {
      const lowerPart = part.toLowerCase().replace(/,/g, '');

      if (monthNames.includes(lowerPart)) {
        try {
          const translated = this.translateService.instant(lowerPart);
          if (part.charAt(0) === part.charAt(0).toUpperCase()) {
            return (
              translated.charAt(0).toUpperCase() +
              translated.slice(1).toLowerCase()
            );
          }
          return translated;
        } catch (error) {
          return part;
        }
      }

      return part;
    });

    return translatedParts.join(' ');
  }
}

@Pipe({
  name: 'transformText',
  pure: false,
  standalone: false,
})
export class TransformTextPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  capitalizeEachWord(str) {
    const words = str.split(' ');
    const capitalizedWords = words.map(
      word => word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase()
    );
    return capitalizedWords.join(' ');
  }

  transform(text, type): any {
    if (typeof text === 'number' && !isNaN(text)) {
      return text;
    }
    return !text || typeof text !== 'string' || text === 'undefined'
      ? ''
      : type === 'uppercase'
        ? text.toUpperCase()
        : type === 'lowercase'
          ? text.toLowerCase()
          : type === 'capitalize'
            ? this.capitalizeEachWord(text)
            : text;
  }
}

@Pipe({
  name: 'showFalsy',
  standalone: false,
})
export class ShowFalsyPipe implements PipeTransform {
  transform(value: any): string {
    if (value === 0) return '0';
    if (value === null) return 'null';
    if (value === false) return 'false';
    if (value === undefined) return '';
    return value;
  }
}

@Pipe({
  name: 'highlight',
  standalone: false,
})
export class HighlightPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string, args: string[]): any {
    if (!value) return value;
    if (!args || args?.length === 0) return value;

    const escapedArgs = args.map(arg =>
      arg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const pattern = escapedArgs
      .map(word => `(?<=\\s|^)['"]?\\s*${word}\\s*['"]?(?=\\s|$)`)
      .join('|');
    const re = new RegExp(pattern, 'gi');
    const text = value.replace(
      re,
      match => `<span class="highlight">${match}</span>`
    );
    return this.sanitizer.bypassSecurityTrustHtml(text);
  }
}

@Pipe({
  name: 'localizeDate',
  standalone: false,
})
export class LocalizeDatePipe implements PipeTransform {
  constructor() {}
  transform(
    value: Date | string | number,
    args: string,
    timeZone?: string
  ): string {
    let local = Intl.DateTimeFormat().resolvedOptions();
    if (!value) return '';
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      value = Number(value);
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return value.toString();
    const settingMap = {
      M: { month: 'numeric' },
      MM: { month: '2-digit' },
      MMM: { month: 'short' },
      MMMM: { month: 'long' },
      d: { day: 'numeric' },
      dd: { day: '2-digit' },
      ddd: { day: 'short' },
      dddd: { day: 'long' },
      D: { day: 'numeric' },
      DD: { day: '2-digit' },
      DDD: { day: 'short' },
      DDDD: { day: 'long' },
      y: { year: '2-digit' },
      yy: { year: '2-digit' },
      yyyy: { year: 'numeric' },
      Y: { year: 'numeric' },
      YY: { year: 'numeric' },
      YYYY: { year: 'numeric' },
      h: { hour: 'numeric' },
      hh: { hour: '2-digit' },
      H: { hour: 'numeric', hour12: false },
      HH: { hour: '2-digit', hour12: false },
      m: { minute: 'numeric' },
      mm: { minute: '2-digit' },
      s: { second: 'numeric' },
      ss: { second: '2-digit' },
      z: { timeZoneName: 'short' },
      zz: { timeZoneName: 'short' },
      zzz: { timeZoneName: 'long' },
      short: {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      },
      medium: {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        month: 'short',
        day: '2-digit',
        year: '2-digit',
      },
      long: {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      },
    };
    let extras = '';
    let options: Intl.DateTimeFormatOptions = {};
    if (timeZone) {
      options.timeZone = timeZone;
    }
    if (args) {
      Object.keys(settingMap).forEach(option => {
        let set = settingMap[option];
        let matcher = new RegExp(option, 'g');
        if (matcher.test(args)) {
          options = { ...options, ...set };
        }
      });
    }
    const formatter = new Intl.DateTimeFormat(local.locale, options);
    if (/z/.test(args) && !/zz/.test(args) && !options.timeZoneName) {
      extras = ' ' + formatter.resolvedOptions().timeZone;
    }
    let output = formatter.format(date);
    return output + extras;
  }
}

@Pipe({
  name: 'pipeFromText',
  standalone: false,
})
export class PipeFromText implements PipeTransform {
  constructor(private localizeDatePipe: LocalizeDatePipe) {}
  transform(text: string): any {
    if (!text) return '';
    const parts = [];
    if (text.includes('[')) {
      const regex = /(\[.*?\])|([^\[\]]+)/g;
      let match;
      while ((match = regex.exec(text)) !== null) {
        parts.push(match[0]);
      }
    } else {
      return text;
    }
    const transformedParts = [];
    parts.forEach(part => {
      if (part.match(/\|\s*date[(|\s]/)) {
        const value = part.split('|')[0].trim().replace(/^\[/, '');
        const args = part.match(/(?<=\()(.*?)(?=\))/g);
        part = this.localizeDatePipe.transform(value, args[0], args[1]);
      }
      transformedParts.push(part);
    });
    return transformedParts.join('');
  }
}
